import { ENTER } from '@angular/cdk/keycodes';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, QueryList,
  ViewChild, ViewChildren
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ApolloQueryResult } from '@apollo/client';
import { QueryRef } from 'apollo-angular';
import { map, takeUntil } from 'rxjs/operators';

import {
  SearchCampaignObjectVisibility, SearchCampaignObjectVisibilityGQL,
  SearchCampaignObjectVisibilityQuery, SearchCampaignsQueryVariables
} from 'generated/graphql';

import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ListItemComponent } from 'shared/components/list-item/list-item.component';
import {
  InfiniteSearchScrollableComponentTemplate, InfiniteSearchScrollAdaptor
} from 'shared/InfiniteSearchScrollAdaptor';

// Label includes html here
// because items inside the span
// are hidden on mobile.
const VISIBLITY_OPT_BOTH = {
  label: 'All<span>&nbsp;campaigns</span>',
  value: 'both'
};
const VISIBLITY_OPT_VISIBLE = {
  label: '<span>Toggled&nbsp;</span>on',
  value: 'visible'
};

const VISIBLITY_OPT_HIDDEN = {
  label: '<span>Toggled&nbsp;</span>off',
  value: 'hidden'
};

/**
 * Generic input which uses the backend API to filter and display
 * if a campaign has (or does not have) access to a specific type
 * of object such as an EntityType, Tag, EntityConnectionType
 */
@Component({
  selector: 'app-campaign-visibility-filter',
  templateUrl: `./campaign-visibility-filter.component.html`
})
export class CampaignVisibilityFilterComponent extends WithDisposableSubject()
  implements OnInit, AfterViewInit, InfiniteSearchScrollableComponentTemplate<SearchCampaignObjectVisibility.Node> {
  // True when data is loading
  loading = false;
  /** Items fetched per page from graphql */
  readonly itemsPerPage = 50;

  /** Adaptor "mixin" to handle common functionality across infinite search compoents */
  private _infiniteSearchScrollAdaptor: InfiniteSearchScrollAdaptor<any>;

  @Input() objectType: string;
  @Input() objectTypeId: string;
  @Output() itemSelected = new EventEmitter<SearchCampaignObjectVisibility.Node>();

  @ViewChild('scrollContainer', { static: true })
  private _scrollContainer: CdkVirtualScrollViewport;

  @ViewChildren(ListItemComponent) items: QueryList<ListItemComponent>;
  filterInput = new UntypedFormControl();

  resultsQuery: QueryRef<SearchCampaignObjectVisibilityQuery, SearchCampaignsQueryVariables>;
  // The retrieved results
  results: SearchCampaignObjectVisibility.Node[] = [];

  selectedVisibilityOption = VISIBLITY_OPT_BOTH;
  visiblityOptions = [
    VISIBLITY_OPT_BOTH,
    VISIBLITY_OPT_VISIBLE,
    VISIBLITY_OPT_HIDDEN
  ];

  private get keyManager() {
    return this._infiniteSearchScrollAdaptor?.keyManager;
  }

  /**
   *
   */
  constructor(
    private _cd: ChangeDetectorRef,
    private _searchVisibilityGQL: SearchCampaignObjectVisibilityGQL
  ) {
    super();
  }

  // **Life Cycle Hooks** //
  ngOnInit() {
    this.resultsQuery = this._searchVisibilityGQL.watch(this.getFetchMoreVariables(0));

    this._infiniteSearchScrollAdaptor = new InfiniteSearchScrollAdaptor(this);

    this._infiniteSearchScrollAdaptor.results.pipe(
      takeUntil(this.disposer)
    ).subscribe(r => {
      this.results = r;
      this._cd.markForCheck();
    });

  }

  ngAfterViewInit() {
    this._infiniteSearchScrollAdaptor.connect();
  }

  getScrollContainer(): CdkVirtualScrollViewport {
    return this._scrollContainer;
  }

  getFetchMoreVariables(page: number) {
    const skipSize = page * this.itemsPerPage;
    return {
      keyword: this.filterInput.value || '',
      objectTypeId: this.objectTypeId,
      objectType: this.objectType as any,
      visibility: this.selectedVisibilityOption.value,
      first: this.itemsPerPage,
      after: page > 0 ? btoa(skipSize.toString()).toString() : null
    };
  }

  selectResults(queryResult: ApolloQueryResult<SearchCampaignObjectVisibility.Query>) {
    return queryResult.data.searchCampaignObjectVisibility.edges.map(n => n.node);
  }

  getChangeDetectorRef(): ChangeDetectorRef {
    return this._cd;
  }

  /**
   * Triggered when user selects a new visiblity option
   *
   * @param opt new options selected
   */
  visibiltyOptionChanged(opt) {
    this.selectedVisibilityOption = opt;
    this._infiniteSearchScrollAdaptor.reset();
  }

  focusResults() {
    this._infiniteSearchScrollAdaptor.keyManager.setFirstItemActive();
  }

  /**
   * Keyboard actions for the results list
   *
   * @param event keyboard event
   */
  onResultsListKeydown(event) {
    if (event.keyCode === ENTER) {
      const item = this._infiniteSearchScrollAdaptor.keyManager.activeItem.item;
      if (item) {
        this.itemClicked(item);
      }
    } else {
      this._infiniteSearchScrollAdaptor.keyManager.onKeydown(event);
    }
  }

  /**
   * Triggered when item is selected
   *
   * @param item Selected item
   */
  itemClicked(item: SearchCampaignObjectVisibility.Node) {
    this.itemSelected.emit(item);
  }
}
